/**
 * useSidebarPinned — persistent sidebar pin state.
 *
 * Authority: WI-UI-CLEANUP-001 Stage 1. §4.2 of
 *            docs/superpowers/plans/2026-04-21-inbox-cockpit-and-sidebar-hover.md
 *
 * Storage: localStorage under SIDEBAR_PINNED_KEY, purged on STATE_EPOCH bump
 *          by UI_SESSION.wipeStaleState in ui-invariants.js.
 *
 * Why localStorage and not a server endpoint: no user preferences collection
 * exists in backend/config/data_province.json today. Plan §6 constraint 5
 * forbids silently adding a schema field. The plan §4.2 explicitly permits
 * localStorage fallback. Server-side persistence will be picked up when a
 * user preferences collection is introduced (out of scope for Stage 1).
 */

import { useCallback, useEffect, useState } from 'react';
import { SIDEBAR_PINNED_KEY } from '@/config/ui-invariants';

const readStoredPin = () => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(SIDEBAR_PINNED_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeStoredPin = (value) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(SIDEBAR_PINNED_KEY, value ? 'true' : 'false');
  } catch {
    // Storage blocked (private mode, quota). Pin becomes session-scoped — acceptable.
  }
};

export function useSidebarPinned() {
  const [isPinned, setIsPinned] = useState(readStoredPin);

  useEffect(() => {
    writeStoredPin(isPinned);
  }, [isPinned]);

  const togglePin = useCallback(() => {
    setIsPinned((prev) => !prev);
  }, []);

  return { isPinned, setIsPinned, togglePin };
}

export default useSidebarPinned;
